/**
 * Pure overflow maths for the module button bar (see components/ModuleNavBar.js).
 *
 * Inputs come from a hidden measurement layer that renders EVERY module button
 * in its natural layout (gaps, hub spacer, admin group box included):
 *   ends[i]   right edge of item i, relative to the start of the bar (px)
 *   widths[i] width of item i's button (px)
 *
 * Rule:
 *  1. If the last item ends within `available`, everything is visible.
 *  2. Otherwise reserve `moreWidth` for the "More ▾" button and keep the longest
 *     leading run of items that ends within the remaining budget. The rest go
 *     into the More menu, in their original order.
 *  3. If the active item would be hidden, trailing visible items are dropped
 *     until the active item (plus `gap`) fits after the run, and it is appended
 *     so the current module is always on the bar.
 */
export function computeModuleNavOverflow({
  ends = [],
  widths = [],
  available = 0,
  moreWidth = 0,
  gap = 8,
  activeIndex = -1,
} = {}) {
  const count = ends.length;
  const all = Array.from({ length: count }, (_, i) => i);
  if (count === 0) return { visible: [], overflow: [] };
  if (!(available > 0) || ends[count - 1] <= available) {
    return { visible: all, overflow: [] };
  }

  const budget = available - moreWidth;
  let fit = 0;
  while (fit < count && ends[fit] <= budget) fit += 1;

  if (activeIndex >= fit && activeIndex < count) {
    const activeWidth = widths[activeIndex] || 0;
    while (fit > 0 && ends[fit - 1] + gap + activeWidth > budget) fit -= 1;
    const visible = [...all.slice(0, fit), activeIndex];
    const overflow = all.filter((i) => i >= fit && i !== activeIndex);
    return { visible, overflow };
  }

  return { visible: all.slice(0, fit), overflow: all.slice(fit) };
}

export default computeModuleNavOverflow;
